import React from 'react';
import { Typography, Paper, Box, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { formatNumber } from '../util/helpers';
import { formatDate } from '../util/helpers';
import QeeHintBox from './qeeHintBox';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        width: '100%', // Ensure the component expands to 100% of its parent's width
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius,
        width: '100%', // Ensure the paper expands to 100% of its parent's width
    },
    subform: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(0),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    totalCost: {
        fontSize: '1.2rem',
        fontWeight: 600,
        paddingTop: theme.spacing(4),
		paddingLeft: theme.spacing(2),
    },
    subtitle: {
        marginTop: theme.spacing(1),
		paddingLeft: theme.spacing(2),		
        color: theme.palette.text.secondary,
    },
    boldText: {
        fontWeight: 'bold',
    },
    costSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    // Adjusted styles for smaller screens
    tableRow: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`, // Row separator
        },
    },
    tableCellSmall: {
        display: 'table-cell', // Default to table-cell behavior for large screens
        paddingBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: { 
            display: 'block',
            width: '100%',  // Ensure full width on small screens
            padding: theme.spacing(1, 0),
            borderBottom: 'none',  // Remove vertical cell separators on small screens
        },
    },
}));

const ContractPreview = ({ ord, sac }) => {
    const classes = useStyles();

    // Use data from contract if it exists, otherwise fallback to offer
    const {
        priceRule = ord.contract?.priceRule || ord.offer?.priceRule || 'flat',
        contractText = ord.contract?.contractText || ord.offer?.desc || '',
        materialCost = ord.contract?.materialCost || ord.offer?.materialCost || 0,
        laborCost = ord.contract?.laborCost || ord.offer?.laborCost || 0,
        otherCosts = ord.contract?.otherCosts || ord.offer?.otherCosts || 0,
        hourlyFee = ord.contract?.hourlyFee || ord.offer?.hourlyFee || 0,
        hours = ord.contract?.hours || ord.offer?.hours || 0,
        totalCost = ord.contract?.totalCost || ord.offer?.totalCost || 0,
        startDate = ord.contract?.startDate,
        endDate = ord.contract?.endDate,
        dateSigned = ord.contract?.dateSigned,
    } = {};

    const config = [
        {
            label: 'Vertragstext:',
            value: contractText,
            cond: !!contractText,
        },
        {
            label: 'Vertragsbeginn:',
            value: formatDate( startDate, 'synapcusDate'),
            cond: !!startDate,
        },
        {
            label: 'Vertragsende:',
            value: formatDate( endDate, 'synapcusDate'),
            cond: !!endDate,
        },
        {
            label: 'Unterschrieben am:',
            value: formatDate( dateSigned, 'synapcusDate'),
            cond: !!dateSigned,
        },
        ...(priceRule === 'flat'
            ? [
                  { label: 'Materialkosten:', value: formatNumber(materialCost, 'EUR', 2), cond: true },
                  { label: 'Lohnkosten:', value: formatNumber(laborCost, 'EUR', 2), cond: true },
                  { label: 'Sonstige Kosten:', value: formatNumber(otherCosts, 'EUR', 2), cond: true },
              ]
            : [
                  { label: 'Stundensatz:', value: formatNumber(hourlyFee, 'EUR', 2), cond: true },
                  { label: 'Anzahl Stunden:', value: hours, cond: true },
              ]),
    ];

    return (
        <Paper className={classes.paper}>
            <Table>
                <TableBody>
                    {config.map(
                        (item, index) =>
                            item.cond && (
                                <TableRow key={index} className={classes.tableRow}>
                                    <TableCell component="th" scope="row" className={classes.tableCellSmall}>
                                        <Box>
                                            <Typography variant="lbl" className={classes.fieldLabel} display="block">
                                                {item.label}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellSmall}>
                                        <Typography variant="body2">
                                            {item.value}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )
                    )}
                </TableBody>
            </Table>
            <Typography variant="h6" className={classes.totalCost}>
                <strong>Gesamtpreis (brutto):</strong> <span className={classes.boldText}>{formatNumber(totalCost, 'EUR', 2)}</span>
            </Typography>
            <Typography className={classes.subtitle}>
                inkl. 19% MwSt.
            </Typography>
            
        </Paper>
    );
};

export default ContractPreview;
